import { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { createCheckoutSession } from '../../Services/APIs/subscription';
import { getClinicSubscriptionStatus } from '../../Services/APIs/appDetails';
import { getFlowDecision } from '../../Utils/clinicFunnel';
import BillingLayout from './BillingLayout';

const Subscription = () => {
    const navigate = useNavigate();
    const [isRedirecting, setIsRedirecting] = useState(false);
    const [subscribeError, setSubscribeError] = useState('');
    const { statusError, retryStatus } = useOutletContext();

    useEffect(() => {
        const handlePageShow = (event) => {
            if (event.persisted) {
                setIsRedirecting(false);
            }
        };
        window.addEventListener('pageshow', handlePageShow);
        return () => window.removeEventListener('pageshow', handlePageShow);
    }, []);

    const handleSubscribe = async () => {
        setSubscribeError('');
        setIsRedirecting(true);
        try {
            const response = await createCheckoutSession();
            if (response?.alreadySubscribed) {
                const status_response = await getClinicSubscriptionStatus();
                const route = getFlowDecision(status_response?.data?.data.status, status_response?.data?.data.subscription_status);
                navigate(route, { replace: true });

                return;
            }
            if (response?.url) {
                window.location.href = response.url;
                return;
            }
            setSubscribeError('Something went wrong. Please try again.');
            setIsRedirecting(false);
        } catch (error) {
            setSubscribeError(error?.response?.data?.message || error?.data?.message || 'Something went wrong. Please try again.');
            setIsRedirecting(false);
        }
    };

    return (
        <BillingLayout
            priceLabel="Price"
            priceCopy="Billed monthly. Cancel anytime."
            footerLabel="Due today"
            footerValue="$499.00"
            actions={
                <>
                    <button
                        type="button"
                        onClick={handleSubscribe}
                        disabled={isRedirecting}
                        className="w-full h-12 bg-claraview-secondary hover:brightness-110 active:brightness-95 disabled:opacity-50 transition-all rounded-[3px] font-spaceMono text-[11px] tracking-[1.2px] text-claraview-quaternary uppercase flex items-center justify-center gap-2"
                    >
                        <Lock className="w-[11px] h-[11px]" strokeWidth={2} />
                        {isRedirecting ? 'Redirecting…' : 'Subscribe'}
                    </button>

                    <p className="cv2-bill-reassurance mt-3.5 text-center font-aeonik text-xs normal-case">
                        256-bit encrypted checkout
                    </p>
                </>
            }
        >
            <div className="mb-10">
                <p className="cv2-bill-eyebrow font-spaceMono text-[10px] tracking-[1.4px] uppercase mb-4">
                    Payment Details
                </p>
                <h2 className="font-aeonik font-normal text-[34px] sm:text-[40px] leading-[40px] sm:leading-[44px] tracking-[-0.9px] text-cv2-foreground normal-case">
                    Complete Your<br />Subscription
                </h2>
            </div>

            {statusError && (
                <div className="cv2-bill-error-banner text-sm border rounded-md px-3 py-2 mb-6">
                    {statusError} This page may not be the right one for your account.
                    <button type="button" onClick={retryStatus} className="block mt-1 underline underline-offset-2">
                        Retry
                    </button>
                </div>
            )}

            {subscribeError && (
                <div className="cv2-bill-error-banner text-sm border rounded-md px-3 py-2 mb-6">
                    {subscribeError}
                </div>
            )}
        </BillingLayout>
    );
};

export default Subscription;
